import type {
  AdoptServiceInput,
  ApiClient,
  ChangePasswordInput,
  ComposeImportResult,
  CorpusBindingInput,
  CreateAgentSessionInput,
  CreateAlertRuleInput,
  CreateConnectionInput,
  CreateDomainInput,
  CreateEnrollTokenInput,
  CreateEnvironmentInput,
  CreateInstanceInput,
  CreatePlacementInput,
  CreateProjectInput,
  CreateLlmCredentialInput,
  CreateProviderCredentialInput,
  CreateSecretInput,
  CreateServiceInput,
  CreateUserInput,
  EnrollToken,
  ExecStreamEndReason,
  ExecStreamHandle,
  ListAgentSessionsParams,
  ListAlertInstancesParams,
  ListAlertRulesParams,
  ListAuditParams,
  ListConnectionsParams,
  ListContainersParams,
  ListDeploymentsParams,
  ListHealthParams,
  ListHostsParams,
  ListInstancesParams,
  ListNodePlacementsParams,
  ListLlmCredentialsParams,
  ListNodesParams,
  ListPlacementsParams,
  ListProjectsParams,
  ListProviderCredentialsParams,
  ListSecretsParams,
  ListServicesParams,
  LoginInput,
  RegisterInput,
  LogStreamEndReason,
  LogStreamHandle,
  GetConnectionStatusesParams,
  OpenExecStreamParams,
  OpenLogStreamParams,
  Page,
  PageParams,
  PromoteServiceInput,
  PromoteSessionInput,
  PromoteSessionResult,
  ProposeImportParams,
  ProviderCatalog,
  RedeployOptions,
  RotateLlmCredentialKeyInput,
  RotateProviderCredentialInput,
  RunSessionTaskInput,
  Session,
  SetNodeEnvironmentInput,
  SSOConfigInput,
  SSOInvite,
  SSOInviteInput,
  SSOTestConnectionInput,
  SSOTestConnectionResult,
  UpdateAlertRuleInput,
  UpdateEnvironmentInput,
  UpdateLlmCredentialConfigInput,
  UpdateNodeInput,
  UpdatePlacementInput,
  UpdateProjectInput,
  UpdateServiceInput,
  UpdateUserInput,
} from "./client";
import type { AuditVerificationStatus } from "./types";
import { ApiError } from "./errors";
import { emitUnauthorized } from "./authEvents";
import { createExecStreamEmitter } from "./execStream";
import { createLogStreamEmitter } from "./logStream";
import type {
  Account,
  AdoptPreview,
  AgentSession,
  AlertInstance,
  AlertRule,
  AuditEvent,
  AuditVerification,
  Connection,
  ConnectionStatus,
  Container,
  ContainerEvent,
  CorpusBinding,
  GovernanceFinding,
  GovernancePolicy,
  CorpusList,
  Deployment,
  DeploymentDetail,
  DeployPreview,
  Domain,
  EntitlementStatus,
  Environment,
  Node,
  Host,
  HealthCell,
  ApplyImportResult,
  ImportProposal,
  Instance,
  LlmCredentialSummary,
  Placement,
  Project,
  ProposedImportItem,
  ProviderCredentialSummary,
  SecretSummary,
  Service,
  SSOConfig,
  Subscription,
  User,
} from "./types";

/**
 * HTTP implementation of `ApiClient` against the real control plane. Every
 * request is a RELATIVE path (`/api/...`) with
 * `credentials: "include"` so the browser attaches/receives the httpOnly
 * session cookie automatically — this file never reads, stores, or sends a
 * bearer token (charter S-3/S-13). Relative paths also mean it works unmodified
 * behind both the vite dev proxy (`vite.config.ts` -> 127.0.0.1:8443) and the
 * nginx proxy in the built image (`nginx.conf`).
 *
 * Every path is TENANT-SCOPED with no tenant segment (ADR-0035 M1 PR-D removed
 * the `/api/fleets/{fleet}` echo): the server derives the tenant from the
 * session cookie (S-17), so there is no tenant id anywhere in these URLs or
 * method signatures.
 */

type ContainerActionKind = "start" | "stop" | "restart";

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  path: string;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  /**
   * A raw (non-JSON) request body plus the exact Content-Type header to send
   * it under — the compose-import route (S-19 on the server: a fail-closed
   * YAML-media-type allow-list, `text/plain` explicitly excluded) is the one
   * caller of this today. Mutually exclusive with `body`: when set, `rawBody`
   * is sent byte-for-byte instead of `JSON.stringify(body)`.
   */
  rawBody?: { text: string; contentType: string };
}

function buildUrl(path: string, query?: Record<string, string | number | undefined>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/** Most of the API surface has converged on the shared JSON error envelope
 * (`httpx.ErrorBody` — `{error, code, requestId}`, server/internal/httpx/
 * httpx.go), but a few older/lower-level paths (dockerproxy, the mesh
 * bridge, obs) still call the stdlib's `http.Error` and answer with plain
 * text. Try the JSON shape first and fall back to raw text so both keep
 * working — this is the one place that distinction is allowed to matter;
 * every caller of `rawRequest`/`request` just sees `ApiError.message`. */
async function safeErrorMessage(res: Response): Promise<string> {
  let text: string;
  try {
    text = (await res.text()).trim();
  } catch {
    return defaultMessageForStatus(res.status);
  }
  if (!text) return defaultMessageForStatus(res.status);

  try {
    const parsed = JSON.parse(text) as { error?: unknown };
    if (typeof parsed.error === "string" && parsed.error.trim()) {
      const message = parsed.error.trim();
      return message.charAt(0).toUpperCase() + message.slice(1);
    }
  } catch {
    // Not JSON — fall through to the plain-text path below.
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function defaultMessageForStatus(status: number): string {
  switch (status) {
    case 400:
      return "That request wasn't valid.";
    case 401:
      return "Your session has expired. Sign in again.";
    case 402:
      return "That plan limit has been reached.";
    case 403:
      return "You don't have permission to do that.";
    case 404:
      return "Not found.";
    case 422:
      return "That request couldn't be processed as sent.";
    case 501:
      return "Not available yet.";
    case 502:
      return "The control plane couldn't reach that node.";
    case 503:
      return "The control plane is unavailable right now.";
    default:
      return "Something went wrong talking to the control plane.";
  }
}

function codeForStatus(status: number): string {
  switch (status) {
    case 401:
      return "unauthenticated";
    case 402:
      return "payment_required";
    case 403:
      return "forbidden";
    case 404:
      return "not_found";
    case 501:
      return "not_implemented";
    default:
      return `http_${status}`;
  }
}

// ---- CSRF double-submit (WEB-M2) ----
// The server mints a non-HttpOnly token cookie alongside the session cookie
// at login (server/internal/auth/http.go) specifically so this client can
// read it and echo it back — reading document.cookie is the intended use,
// not a workaround. Only a mutating request needs the header; GET never
// changes state, so it's never CSRF-relevant.
const CSRF_COOKIE_NAME = "comebye_csrf";
const CSRF_HEADER_NAME = "X-CSRF-Token";
const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function readCsrfCookie(): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE_NAME}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

/** Performs the fetch, maps a non-2xx response to a thrown `ApiError`, and
 * returns the raw response body text (empty for a 204/205). Every other helper
 * in this file builds on this one so error handling and 401 detection stay in
 * exactly one place. */
async function rawRequest(options: RequestOptions): Promise<string> {
  const { method = "GET", path, body, query, rawBody } = options;
  const url = buildUrl(path, query);

  const headers: Record<string, string> = {};
  if (rawBody !== undefined) {
    headers["Content-Type"] = rawBody.contentType;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (MUTATING_METHODS.has(method)) {
    const csrfToken = readCsrfCookie();
    // No cookie means no session either (they're minted together) — omit
    // the header rather than send an empty one; the server's fail-closed
    // check treats "missing" and "blank" identically anyway.
    if (csrfToken) headers[CSRF_HEADER_NAME] = csrfToken;
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: "include",
      headers: Object.keys(headers).length > 0 ? headers : undefined,
      body: rawBody !== undefined ? rawBody.text : body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(
      "Couldn't reach the control plane. Check your connection and try again.",
      0,
      "network_error",
    );
  }

  if (!res.ok) {
    // A 401 on any authenticated call (never on /login itself, where it just
    // means "wrong credentials" — nor on /auth/session, where it just means
    // "not signed in yet", the routine case getSession() exists to handle
    // quietly) means the session cookie is missing/expired server-side. Tell
    // AuthContext so it clears state and ProtectedRoute sends the operator
    // back to /login — see authEvents.ts.
    if (res.status === 401 && path !== "/api/auth/login" && path !== "/api/auth/session") {
      emitUnauthorized();
    }
    throw new ApiError(await safeErrorMessage(res), res.status, codeForStatus(res.status));
  }

  return res.text();
}

async function request<T>(options: RequestOptions): Promise<T> {
  const text = await rawRequest(options);
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

const KNOWN_AUDIT_VERIFICATION_STATUSES: readonly AuditVerificationStatus[] = ["ok", "broken", "unverifiable"];

// parseAuditVerification narrows the raw JSON `/verify` response into an
// AuditVerification instead of trusting an unchecked `as AuditVerification` cast (GH-73
// audit round 5, MEDIUM-3). A version-skewed server, or a proxy/gateway that drops or
// corrupts fields, could hand back a `status` this client build doesn't recognize (or
// none at all) — an unchecked cast would let that value flow straight into
// VerifyResultBanner's switch, and any FUTURE branch written as an `else` (the exact bug
// this round's audit found there) would render it as a green "chain intact" banner.
// Fail closed here instead: an unrecognized/missing status is normalized to
// "unverifiable" — never silently trusted as "ok" — and `ok` is forced false unless the
// server both claims `ok: true` AND a recognized `status: "ok"` agree with each other.
function parseAuditVerification(raw: unknown): AuditVerification {
  const r = (raw && typeof raw === "object" ? raw : {}) as Partial<AuditVerification>;
  const status = KNOWN_AUDIT_VERIFICATION_STATUSES.includes(r.status as AuditVerificationStatus)
    ? (r.status as AuditVerificationStatus)
    : "unverifiable";
  return {
    ...r,
    status,
    ok: status === "ok" && r.ok === true,
    count: typeof r.count === "number" ? r.count : 0,
    tenant: typeof r.tenant === "string" ? r.tenant : "",
  };
}

// ---- Tenant-scoped path helpers (ADR-0035 M1 PR-D: no `/api/fleets/{fleet}`
// segment; the tenant comes only from the session cookie, S-17). ----

function containersPath(): string {
  return `/api/containers`;
}

function containerPath(nodeId: string, containerId: string): string {
  return `${containersPath()}/${encodeURIComponent(nodeId)}/${encodeURIComponent(containerId)}`;
}

function containerLogsStreamUrl(nodeId: string, containerId: string, tail?: number): string {
  return buildUrl(`${containerPath(nodeId, containerId)}/logs/stream`, { tail });
}

// Endpoint path only — not yet backed by a server route (spike doc: "the
// browser WS bridge (C-8) doesn't exist server-side either"). Owned jointly
// with whoever builds the MeshRouter.OpenExec bridge; the path and the
// resize-as-text/stdout-as-binary frame split below are this client's
// proposal, not an agreed contract yet — expect this to change once that
// handshake happens.
function containerExecStreamUrl(nodeId: string, containerId: string): string {
  return `${containerPath(nodeId, containerId)}/exec/stream`;
}

// Exported so the reconnect-cap test can assert against the real value
// rather than a hardcoded copy of it.
export const MAX_LOG_STREAM_RECONNECT_ATTEMPTS = 5;

function isLogStreamEndReason(value: unknown): value is LogStreamEndReason {
  return value === "stopped" || value === "truncated" || value === "error";
}

function isExecStreamEndReason(value: unknown): value is ExecStreamEndReason {
  return value === "exited" || value === "error" || value === "closed";
}

function secretsPath(): string {
  return `/api/secrets`;
}

function secretPath(secretId: string): string {
  return `${secretsPath()}/${encodeURIComponent(secretId)}`;
}

// ---- Admin/user portal path helpers ----

function usersPath(): string {
  return "/api/users";
}

function userPath(id: string): string {
  return `${usersPath()}/${encodeURIComponent(id)}`;
}

function auditPath(): string {
  return `/api/audit-events`;
}

// ---- First-class environments (ADR-0035 §1, M1 PR-E) path helpers ----
// Tenant-scoped (`/api/environments`): the tenant comes only from the session (S-17).

function environmentsPath(): string {
  return "/api/environments";
}

function environmentPath(envId: string): string {
  return `${environmentsPath()}/${encodeURIComponent(envId)}`;
}

function environmentPromotePath(envId: string): string {
  return `${environmentPath(envId)}/promote`;
}

// ---- Hero flow path helpers. Mirrors the real route table exactly
// (server/internal/httpapi/server.go's WithProjects/WithProvisioning
// mounts) ----

function projectsPath(): string {
  return `/api/projects`;
}

function projectPath(projectId: string): string {
  return `${projectsPath()}/${encodeURIComponent(projectId)}`;
}

function projectServicesPath(projectId: string): string {
  return `${projectPath(projectId)}/services`;
}

function servicePath(projectId: string, serviceId: string): string {
  return `${projectServicesPath(projectId)}/${encodeURIComponent(serviceId)}`;
}

function serviceDeploymentsPath(projectId: string, serviceId: string): string {
  return `${servicePath(projectId, serviceId)}/deployments`;
}

function deploymentPath(projectId: string, serviceId: string, deploymentId: string): string {
  return `${serviceDeploymentsPath(projectId, serviceId)}/${encodeURIComponent(deploymentId)}`;
}

// Mirrors composeImportContentTypes (server/internal/projectsvc/composeimport_http.go)
// exactly — "text/yaml" is what real compose tooling and this UI's file picker both
// produce, and the server's allow-list deliberately excludes "text/plain" (a
// CORS-simple content-type a cross-site form could send without a preflight, S-8).
const COMPOSE_IMPORT_CONTENT_TYPE = "text/yaml";

function projectComposeImportPath(projectId: string): string {
  return `${projectPath(projectId)}/compose/import`;
}

// ---- Corpus (ADR-0052 read / ADR-0053 §3 binding). Mirrors the route table in
// server/internal/httpapi/server.go's corpus/corpus-binding mounts exactly. ----

function projectCorpusPath(projectId: string): string {
  return `${projectPath(projectId)}/corpus`;
}

function projectCorpusBindingPath(projectId: string): string {
  return `${projectCorpusPath(projectId)}/binding`;
}

// ---- Governing-agent findings (ADR-0059). Mirrors the route table in
// server/internal/httpapi/server.go's governance-finding mounts exactly. ----

function projectGovernanceFindingsPath(projectId: string): string {
  return `${projectPath(projectId)}/governance/findings`;
}

function resolveGovernanceFindingPath(id: string): string {
  return `/governance/findings/${encodeURIComponent(id)}/resolve`;
}

// ---- Governance policy introspection (ADR-0058, #571). Mirrors the route table in
// server/internal/httpapi/server.go's governance-policy mount exactly. ----

function projectGovernancePolicyPath(projectId: string): string {
  return `${projectPath(projectId)}/governance/policy`;
}

// ---- The Placement Graph (ADR-0025) path helpers. Mirrors the route table
// in docs/decisions/0025-placement-graph.md §2 exactly. ----

function servicePlacementsPath(projectId: string, serviceId: string): string {
  return `${servicePath(projectId, serviceId)}/placements`;
}

function placementPath(projectId: string, serviceId: string, placementId: string): string {
  return `${servicePlacementsPath(projectId, serviceId)}/${encodeURIComponent(placementId)}`;
}

function projectConnectionsPath(projectId: string): string {
  return `${projectPath(projectId)}/connections`;
}

function connectionPath(projectId: string, connectionId: string): string {
  return `${projectConnectionsPath(projectId)}/${encodeURIComponent(connectionId)}`;
}

function serviceDomainsPath(projectId: string, serviceId: string): string {
  return `${servicePath(projectId, serviceId)}/domains`;
}

function domainPath(projectId: string, serviceId: string, domainId: string): string {
  return `${serviceDomainsPath(projectId, serviceId)}/${encodeURIComponent(domainId)}`;
}

function nodePath(nodeId: string): string {
  return `/api/nodes/${encodeURIComponent(nodeId)}`;
}

function nodePlacementsPath(nodeId: string): string {
  return `${nodePath(nodeId)}/placements`;
}

function hostsPath(): string {
  return `/api/hosts`;
}

function hostPath(id: string): string {
  return `${hostsPath()}/${encodeURIComponent(id)}`;
}

function providerCredentialsPath(): string {
  return `/api/provider-credentials`;
}

function providerCredentialPath(id: string): string {
  return `${providerCredentialsPath()}/${encodeURIComponent(id)}`;
}

function llmCredentialsPath(): string {
  return `/api/llm-credentials`;
}

function llmCredentialPath(id: string): string {
  return `${llmCredentialsPath()}/${encodeURIComponent(id)}`;
}

function agentSessionsPath(): string {
  return `/api/agent-sessions`;
}

function agentSessionPath(id: string): string {
  return `${agentSessionsPath()}/${encodeURIComponent(id)}`;
}

function providerCatalogPath(): string {
  return `/api/provider-catalog`;
}

function instancesPath(): string {
  return `/api/instances`;
}

function instancePath(id: string): string {
  return `${instancesPath()}/${encodeURIComponent(id)}`;
}

function proposeImportPath(): string {
  return `/api/import/propose`;
}

function applyImportPath(): string {
  return `/api/import/apply`;
}

// ---- Fleet health matrix (ADR-0030 P4-2) path helper ----

function healthPath(): string {
  return `/api/health`;
}

// ---- Fleet-health alerting (ADR-0030 P4-5) path helpers. Mirrors
// server/internal/httpapi/server.go's WithAlerts mount exactly. ----

function alertsPath(): string {
  return `/api/alerts`;
}

function alertRulePath(id: string): string {
  return `${alertsPath()}/${encodeURIComponent(id)}`;
}

function alertInstancesPath(ruleId: string): string {
  return `${alertRulePath(ruleId)}/instances`;
}

/**
 * Encodes a plaintext signing secret as standard base64 for the wire
 * (`alertsvc.decodeSigningSecret` expects base64 — S-14: the plaintext
 * itself never appears in a URL, only in this request body). UTF-8 safe
 * (goes through bytes, not `btoa` on the raw string directly, which chokes
 * on non-Latin1 characters). An empty string encodes to itself — the
 * server's "" special-cases to "no signing / clear", so there's nothing to
 * encode.
 */
function encodeSigningSecretBase64(secret: string): string {
  if (!secret) return "";
  const bytes = new TextEncoder().encode(secret);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

async function containerAction(
  nodeId: string,
  containerId: string,
  action: ContainerActionKind,
): Promise<Container> {
  const text = await rawRequest({ method: "POST", path: `${containerPath(nodeId, containerId)}/${action}` });
  if (text) return JSON.parse(text) as Container;
  // The server falls back to a bare 204 when the action itself succeeded but its
  // own post-action re-inspect couldn't complete (nodesvc.ContainerActionHandler).
  // Re-fetch so the caller still gets the Container the contract promises.
  return request<Container>({ path: containerPath(nodeId, containerId) });
}

export function createRealApiClient(): ApiClient {
  return {
    async login({ username, password }: LoginInput): Promise<Session> {
      return request<Session>({ method: "POST", path: "/api/auth/login", body: { username, password } });
    },

    async register({ username, password, displayName }: RegisterInput): Promise<Session> {
      return request<Session>({ method: "POST", path: "/register", body: { username, password, displayName } });
    },

    async logout(): Promise<void> {
      await request<void>({ method: "POST", path: "/api/auth/logout" });
    },

    async getSession(): Promise<Session | null> {
      // See ApiClient.getSession's doc comment: this route does not exist on
      // the real server yet. Every failure (404 today; a genuine 401 once it
      // does exist and there's no cookie; a network error) resolves null —
      // there's nothing "the caller is signed in" to recover, and this call
      // is opportunistic (AuthContext only ever falls back to today's
      // behavior on null), never something to surface as an error toast.
      try {
        return await request<Session>({ path: "/api/auth/session" });
      } catch {
        return null;
      }
    },

    async listContainers(params: ListContainersParams): Promise<Page<Container>> {
      return request<Page<Container>>({
        path: containersPath(),
        query: {
          nodeId: params.nodeId,
          // Only sent when true — mirrors the server's filter (omitted/false means "every
          // container", ADR-0038 P4a), and buildUrl already drops undefined entries.
          unmanaged: params.unmanaged ? "true" : undefined,
          // Mirrors the server's `?serviceId=` filter (#408); buildUrl drops it when undefined.
          serviceId: params.serviceId,
          cursor: params.cursor,
          limit: params.limit,
        },
      });
    },

    async getContainer(nodeId: string, containerId: string): Promise<Container> {
      return request<Container>({ path: containerPath(nodeId, containerId) });
    },

    async startContainer(nodeId: string, containerId: string): Promise<Container> {
      return containerAction(nodeId, containerId, "start");
    },

    async stopContainer(nodeId: string, containerId: string): Promise<Container> {
      return containerAction(nodeId, containerId, "stop");
    },

    async restartContainer(nodeId: string, containerId: string): Promise<Container> {
      return containerAction(nodeId, containerId, "restart");
    },

    async listNodes(params: ListNodesParams): Promise<Page<Node>> {
      return request<Page<Node>>({
        path: `/api/nodes`,
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    // ---- Hosts: the unified fleet object (host/deploy UX review, #526) ----
    async listHosts(params: ListHostsParams): Promise<Page<Host>> {
      return request<Page<Host>>({
        path: hostsPath(),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async getHost(id: string): Promise<Host> {
      return request<Host>({ path: hostPath(id) });
    },

    async updateNode(nodeId: string, input: UpdateNodeInput): Promise<Node> {
      return request<Node>({
        method: "PATCH",
        path: nodePath(nodeId),
        body: input,
      });
    },

    async setNodeEnvironment(nodeId: string, input: SetNodeEnvironmentInput): Promise<Node> {
      return request<Node>({
        method: "PATCH",
        path: `${nodePath(nodeId)}/environment`,
        body: input,
      });
    },

    // ---- Secrets (C-2b): write-only, live server-side ----
    // server/internal/secretsvc mounts these under /api/secrets (tenant from
    // the session). The wire shape (SecretMetaJSON) has no value field
    // anywhere, on any of these responses — see types.ts.

    async listSecrets(params: ListSecretsParams): Promise<Page<SecretSummary>> {
      return request<Page<SecretSummary>>({
        path: secretsPath(),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async createSecret(input: CreateSecretInput): Promise<SecretSummary> {
      return request<SecretSummary>({
        method: "POST",
        path: secretsPath(),
        body: input,
      });
    },

    async rotateSecret(secretId: string, value: string): Promise<SecretSummary> {
      return request<SecretSummary>({
        method: "POST",
        path: `${secretPath(secretId)}/rotate`,
        body: { value },
      });
    },

    async deleteSecret(secretId: string): Promise<void> {
      await request<void>({
        method: "DELETE",
        path: secretPath(secretId),
      });
    },

    // ---- Enrollment (C-6) ----

    async createEnrollToken(input: CreateEnrollTokenInput): Promise<EnrollToken> {
      // The server derives the tenant from the session and returns
      // id/agentId/label/tenant; `token`/`expiresAt`/`agentId` (when the caller
      // pre-assigned one, PR #634 audit condition 5) are the fields lifted
      // into the client-facing shape (see EnrollToken in client.ts).
      // `environment` is required server-side (createTokenRequest) — sent
      // through verbatim, never defaulted here.
      return request<EnrollToken>({
        method: "POST",
        path: "/api/enroll/tokens",
        body: input,
      });
    },

    // ---- Live log tail (C-8a) ----

    openLogStream({ nodeId, containerId, tail }: OpenLogStreamParams): LogStreamHandle {
      const url = containerLogsStreamUrl(nodeId, containerId, tail);
      // `withCredentials` is belt-and-suspenders: this URL is always relative
      // (see the file header), so the browser already attaches the httpOnly
      // session cookie same-origin without it — matches `credentials:
      // "include"` on the fetch path above. Never a token in the URL (S-13).
      const source = new EventSource(url, { withCredentials: true });
      const emitter = createLogStreamEmitter(() => source.close());
      let failures = 0;

      source.addEventListener("log", (evt) => {
        const raw = (evt as MessageEvent<string>).data;
        try {
          const parsed = JSON.parse(raw) as { s?: unknown; m?: unknown };
          if (parsed.s === "stdout" || parsed.s === "stderr") {
            emitter.emitLine({ stream: parsed.s, message: typeof parsed.m === "string" ? parsed.m : "" });
          }
        } catch {
          // Malformed frame from the server — drop this one line rather than
          // take the whole panel down over it.
        }
      });

      source.addEventListener("end", (evt) => {
        const raw = (evt as MessageEvent<string>).data;
        let reason: LogStreamEndReason = "stopped";
        try {
          const parsed = JSON.parse(raw) as { reason?: unknown };
          if (isLogStreamEndReason(parsed.reason)) reason = parsed.reason;
        } catch {
          // Fall back to "stopped" — still a clean, expected close either way.
        }
        emitter.emitEnd(reason);
      });

      source.onopen = () => {
        failures = 0;
        emitter.emitStatus("open");
      };

      // EventSource retries on its own after any non-clean close (network
      // blip, server restart, proxy idle timeout) — desired, but silent
      // infinite retry is not, so this caps it and surfaces the in-between
      // state via `onStatus` rather than spinning quietly. Note: because a
      // reconnect re-tails from `tail=N`, an operator may briefly see a few
      // recent lines repeat after one — acceptable for this MVP.
      source.onerror = () => {
        if (source.readyState === EventSource.CLOSED) {
          // Either we closed it ourselves (close()/emitEnd already handled
          // teardown) or the browser gave up for good with no more retries.
          emitter.emitStatus("closed");
          return;
        }
        failures += 1;
        if (failures > MAX_LOG_STREAM_RECONNECT_ATTEMPTS) {
          source.close();
          emitter.emitStatus("closed");
          emitter.emitEnd("error");
          return;
        }
        emitter.emitStatus("reconnecting");
      };

      return emitter;
    },

    // ---- Interactive exec (SPIKE-3 / C-8) ----
    // Provisional wire framing pending agreement with whoever builds the
    // server-side WS<->Session bridge (see containerExecStreamUrl): stdin
    // and stdout ride binary WS frames verbatim (no envelope — proto/exec.go
    // already frames stdin/stdout on the tunnel side of the bridge, so this
    // client doesn't need to re-frame it), while resize and the terminal
    // end-of-session signal ride distinct text (JSON) frames so the binary
    // path stays a straight byte pass-through end to end (P-3 latency
    // budget: no base64, no JSON parsing on the keystroke round-trip).
    openExecStream({ nodeId, containerId, cols, rows }: OpenExecStreamParams): ExecStreamHandle {
      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${wsProtocol}//${window.location.host}${buildUrl(containerExecStreamUrl(nodeId, containerId), { cols, rows })}`;
      const socket = new WebSocket(url);
      socket.binaryType = "arraybuffer";

      const emitter = createExecStreamEmitter(
        () => socket.close(),
        (data) => {
          if (socket.readyState === WebSocket.OPEN) socket.send(data);
        },
        (nextCols, nextRows) => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "resize", cols: nextCols, rows: nextRows }));
          }
        },
      );

      socket.onopen = () => emitter.emitStatus("open");

      socket.onmessage = (evt) => {
        if (evt.data instanceof ArrayBuffer) {
          emitter.emitOutput(new Uint8Array(evt.data));
          return;
        }
        try {
          const parsed = JSON.parse(evt.data as string) as { type?: unknown; reason?: unknown };
          if (parsed.type === "end") {
            emitter.emitEnd(isExecStreamEndReason(parsed.reason) ? parsed.reason : "closed");
          }
        } catch {
          // Not a control frame this client understands — ignore rather
          // than tear down a session over one malformed message.
        }
      };

      socket.onclose = (evt) => {
        // A clean server-initiated close (code 1000) without a preceding
        // "end" control frame is treated the same as one — either way the
        // session is over and there's nothing left to reconnect (unlike
        // openLogStream, an exec session doesn't auto-resume after a drop;
        // the shell process on the other end is gone).
        emitter.emitStatus("closed");
        emitter.emitEnd(evt.code === 1000 ? "exited" : "error");
      };

      socket.onerror = () => {
        // The close event still follows an error, so emitEnd happens there
        // — this only needs to reflect the in-between state.
        emitter.emitStatus("reconnecting");
      };

      return emitter;
    },

    subscribeToContainerEvents(_onEvent: (event: ContainerEvent) => void): () => void {
      // P-1 seam, deliberately deferred: the real control plane has no SSE/WS
      // event stream yet, so this is a no-op that never calls onEvent. Container
      // state reaches the UI instead via the direct cache write on each
      // start/stop/restart mutation and a modest refetchInterval on the
      // containers list (both in queries.ts) — a stand-in, not a replacement for
      // the real thing. When the event channel lands, implement a real
      // subscribe/unsubscribe here (open the channel, call onEvent per message,
      // close it on unsubscribe) and no caller anywhere else needs to change.
      return () => {};
    },

    // ---- Admin: users (admin/user portal) ----

    async listUsers(params: PageParams): Promise<Page<User>> {
      return request<Page<User>>({
        path: usersPath(),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async createUser(input: CreateUserInput): Promise<User> {
      return request<User>({ method: "POST", path: usersPath(), body: input });
    },

    async updateUser(id: string, input: UpdateUserInput): Promise<User> {
      return request<User>({ method: "PATCH", path: userPath(id), body: input });
    },

    async resetUserPassword(id: string, password: string): Promise<void> {
      await request<void>({ method: "POST", path: `${userPath(id)}/password`, body: { password } });
    },

    async deleteUser(id: string): Promise<void> {
      await request<void>({ method: "DELETE", path: userPath(id) });
    },

    // ---- OIDC / SSO admin (ADR-0037 PR-2b) ----

    async getSSOConfig(): Promise<SSOConfig> {
      return request<SSOConfig>({ path: "/api/sso/config" });
    },

    async updateSSOConfig(input: SSOConfigInput): Promise<SSOConfig> {
      return request<SSOConfig>({ method: "PUT", path: "/api/sso/config", body: input });
    },

    async testSSOConnection(input: SSOTestConnectionInput): Promise<SSOTestConnectionResult> {
      // See ApiClient.testSSOConnection's doc comment: this route does not exist on
      // the real server yet (404 today). Render whatever message comes back —
      // never fabricate a passing result on failure.
      return request<SSOTestConnectionResult>({ method: "POST", path: "/api/sso/config/test", body: input });
    },

    async createSSOInvite(input: SSOInviteInput): Promise<SSOInvite> {
      return request<SSOInvite>({ method: "POST", path: "/api/sso/invitations", body: input });
    },

    // ---- Account (the caller's own profile/credential) ----

    async getAccount(): Promise<Account> {
      return request<Account>({ path: "/api/account" });
    },

    async changePassword(input: ChangePasswordInput): Promise<void> {
      await request<void>({ method: "POST", path: "/api/account/password", body: input });
    },

    // ---- Audit log (C-13 — already live server-side) ----
    // server/internal/auditsvc mounts these under /api/audit-events (tenant from the
    // session). Same cursor/limit pagination shape as every other list.

    async listAuditEvents(params: ListAuditParams): Promise<Page<AuditEvent>> {
      return request<Page<AuditEvent>>({
        path: auditPath(),
        // `action` mirrors the server's `?action=` filter (buildUrl drops it when
        // undefined) — the Governance Decisions view passes "governance.decision"
        // here rather than fetching the whole trail and filtering client-side.
        query: { cursor: params.cursor, limit: params.limit, action: params.action },
      });
    },

    async verifyAuditChain(): Promise<AuditVerification> {
      const raw = await request<unknown>({ path: `${auditPath()}/verify` });
      return parseAuditVerification(raw);
    },

    // ---- Hero flow: projects (#63 projectsvc — real, merged route table) ----

    async listProjects(params: ListProjectsParams): Promise<Page<Project>> {
      return request<Page<Project>>({
        path: projectsPath(),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async getProject(id: string): Promise<Project> {
      return request<Project>({ path: projectPath(id) });
    },

    async createProject(input: CreateProjectInput): Promise<Project> {
      return request<Project>({ method: "POST", path: projectsPath(), body: input });
    },

    async updateProject(id: string, input: UpdateProjectInput): Promise<Project> {
      return request<Project>({ method: "PATCH", path: projectPath(id), body: input });
    },

    async deleteProject(id: string): Promise<void> {
      await request<void>({ method: "DELETE", path: projectPath(id) });
    },

    // ---- Corpus (ADR-0052 read / ADR-0053 §3 binding) ----

    async getCorpus(projectId: string, params: PageParams = {}): Promise<CorpusList> {
      return request<CorpusList>({
        path: projectCorpusPath(projectId),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async getCorpusBinding(projectId: string): Promise<CorpusBinding> {
      return request<CorpusBinding>({ path: projectCorpusBindingPath(projectId) });
    },

    async createCorpusBinding(projectId: string, input: CorpusBindingInput): Promise<CorpusBinding> {
      return request<CorpusBinding>({ method: "POST", path: projectCorpusBindingPath(projectId), body: input });
    },

    async updateCorpusBinding(projectId: string, input: CorpusBindingInput): Promise<CorpusBinding> {
      return request<CorpusBinding>({ method: "PUT", path: projectCorpusBindingPath(projectId), body: input });
    },

    async listGovernanceFindings(projectId: string, params: PageParams = {}): Promise<Page<GovernanceFinding>> {
      return request<Page<GovernanceFinding>>({
        path: projectGovernanceFindingsPath(projectId),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async resolveGovernanceFinding(id: string, note: string): Promise<GovernanceFinding> {
      return request<GovernanceFinding>({
        method: "POST",
        path: resolveGovernanceFindingPath(id),
        body: { note },
      });
    },

    async getGovernancePolicy(projectId: string): Promise<GovernancePolicy> {
      return request<GovernancePolicy>({ path: projectGovernancePolicyPath(projectId) });
    },

    // ---- Hero flow: services (create/update/deploy/deployment history) ----

    async listServices(params: ListServicesParams): Promise<Page<Service>> {
      return request<Page<Service>>({
        path: projectServicesPath(params.projectId),
        // Mirrors the server's `?createdBy=` filter (#408); buildUrl drops it when undefined.
        query: { createdBy: params.createdBy, cursor: params.cursor, limit: params.limit },
      });
    },

    async getService(projectId: string, id: string): Promise<Service> {
      return request<Service>({ path: servicePath(projectId, id) });
    },

    async createService(projectId: string, input: CreateServiceInput): Promise<Service> {
      return request<Service>({
        method: "POST",
        path: projectServicesPath(projectId),
        body: input,
      });
    },

    async updateService(
      projectId: string,
      id: string,
      input: UpdateServiceInput,
    ): Promise<Service> {
      return request<Service>({
        method: "PATCH",
        path: servicePath(projectId, id),
        body: input,
      });
    },

    async deleteService(projectId: string, id: string): Promise<void> {
      await request<void>({ method: "DELETE", path: servicePath(projectId, id) });
    },

    async adoptService(projectId: string, input: AdoptServiceInput): Promise<Service> {
      return request<Service>({
        method: "POST",
        path: `${projectServicesPath(projectId)}/adopt`,
        body: { nodeId: input.nodeId, containerId: input.containerId },
      });
    },

    async adoptPreview(projectId: string, input: AdoptServiceInput): Promise<AdoptPreview> {
      return request<AdoptPreview>({
        method: "POST",
        path: `${projectServicesPath(projectId)}/adopt/preview`,
        body: { nodeId: input.nodeId, containerId: input.containerId },
      });
    },

    async importCompose(projectId: string, composeText: string): Promise<ComposeImportResult> {
      return request<ComposeImportResult>({
        method: "POST",
        path: projectComposeImportPath(projectId),
        rawBody: { text: composeText, contentType: COMPOSE_IMPORT_CONTENT_TYPE },
      });
    },

    async deployService(projectId: string, id: string): Promise<Deployment> {
      return request<Deployment>({
        method: "POST",
        path: `${servicePath(projectId, id)}/deploy`,
        body: {},
      });
    },

    async listDeployments(params: ListDeploymentsParams): Promise<Page<Deployment>> {
      return request<Page<Deployment>>({
        path: serviceDeploymentsPath(params.projectId, params.serviceId),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async getDeployment(projectId: string, serviceId: string, deploymentId: string): Promise<DeploymentDetail> {
      return request<DeploymentDetail>({ path: deploymentPath(projectId, serviceId, deploymentId) });
    },

    async redeployDeployment(
      projectId: string,
      serviceId: string,
      deploymentId: string,
      options?: RedeployOptions,
    ): Promise<Deployment> {
      return request<Deployment>({
        method: "POST",
        path: `${deploymentPath(projectId, serviceId, deploymentId)}/redeploy`,
        body: options ?? {},
      });
    },

    async rollbackDeployment(
      projectId: string,
      serviceId: string,
      deploymentId: string,
      options?: RedeployOptions,
    ): Promise<Deployment> {
      return request<Deployment>({
        method: "POST",
        path: `${deploymentPath(projectId, serviceId, deploymentId)}/rollback`,
        body: options ?? {},
      });
    },

    async getDeployPreview(projectId: string, serviceId: string): Promise<DeployPreview> {
      return request<DeployPreview>({ path: `${servicePath(projectId, serviceId)}/deploy/preview` });
    },

    // ---- The Placement Graph (ADR-0025): placements ----

    async listPlacements(params: ListPlacementsParams): Promise<Page<Placement>> {
      return request<Page<Placement>>({
        path: servicePlacementsPath(params.projectId, params.serviceId),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async createPlacement(
      projectId: string,
      serviceId: string,
      input: CreatePlacementInput,
    ): Promise<Placement> {
      return request<Placement>({
        method: "POST",
        path: servicePlacementsPath(projectId, serviceId),
        body: input,
      });
    },

    async updatePlacement(
      projectId: string,
      serviceId: string,
      placementId: string,
      input: UpdatePlacementInput,
    ): Promise<Placement> {
      return request<Placement>({
        method: "PATCH",
        path: placementPath(projectId, serviceId, placementId),
        body: input,
      });
    },

    async deletePlacement(projectId: string, serviceId: string, placementId: string): Promise<void> {
      await request<void>({ method: "DELETE", path: placementPath(projectId, serviceId, placementId) });
    },

    async listNodePlacements(params: ListNodePlacementsParams): Promise<Page<Placement>> {
      return request<Page<Placement>>({
        path: nodePlacementsPath(params.nodeId),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    // ---- The Placement Graph (ADR-0025): connections ----

    async listConnections(params: ListConnectionsParams): Promise<Page<Connection>> {
      return request<Page<Connection>>({
        path: projectConnectionsPath(params.projectId),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async createConnection(projectId: string, input: CreateConnectionInput): Promise<Connection> {
      return request<Connection>({
        method: "POST",
        path: projectConnectionsPath(projectId),
        body: input,
      });
    },

    async deleteConnection(projectId: string, connectionId: string): Promise<void> {
      await request<void>({ method: "DELETE", path: connectionPath(projectId, connectionId) });
    },

    // ADR-0029 P3-5 — a project-level BATCH route, one call per project
    // (not per-connection). Callers (see queries.ts's useConnectionStatuses)
    // MUST catch a failure here (this endpoint may still be racing P3-5's
    // own merge, or any other transport error) and treat every row as
    // "unknown" rather than surfacing a page-level error.
    async getConnectionStatuses(params: GetConnectionStatusesParams): Promise<ConnectionStatus[]> {
      return request<ConnectionStatus[]>({
        path: `${projectConnectionsPath(params.projectId)}/status`,
      });
    },

    // ---- Service domain bindings (ADR-0026 §3/§5) ----

    async listDomains(
      projectId: string,
      serviceId: string,
      params: PageParams = {},
    ): Promise<Page<Domain>> {
      return request<Page<Domain>>({
        path: serviceDomainsPath(projectId, serviceId),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async createDomain(
      projectId: string,
      serviceId: string,
      input: CreateDomainInput,
    ): Promise<Domain> {
      return request<Domain>({
        method: "POST",
        path: serviceDomainsPath(projectId, serviceId),
        body: input,
      });
    },

    async deleteDomain(projectId: string, serviceId: string, domainId: string): Promise<void> {
      await request<void>({ method: "DELETE", path: domainPath(projectId, serviceId, domainId) });
    },

    // ---- Hero flow: provider credentials (write-only — S-13). Mounted only
    // when COMEBYE_PROVISIONING=1; unmounted, these fall through to the
    // catch-all and answer a plain-text 501 (see safeErrorMessage above) —
    // callers should treat that as "not enabled," not a bug. ----

    async listProviderCredentials(params: ListProviderCredentialsParams): Promise<Page<ProviderCredentialSummary>> {
      return request<Page<ProviderCredentialSummary>>({
        path: providerCredentialsPath(),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async createProviderCredential(
      input: CreateProviderCredentialInput,
    ): Promise<ProviderCredentialSummary> {
      return request<ProviderCredentialSummary>({
        method: "POST",
        path: providerCredentialsPath(),
        body: input,
      });
    },

    async rotateProviderCredential(
      id: string,
      input: RotateProviderCredentialInput,
    ): Promise<ProviderCredentialSummary> {
      return request<ProviderCredentialSummary>({
        method: "PUT",
        path: providerCredentialPath(id),
        body: input,
      });
    },

    async deleteProviderCredential(id: string): Promise<void> {
      await request<void>({ method: "DELETE", path: providerCredentialPath(id) });
    },

    // ---- First-class environments (ADR-0035 §1, M1 PR-E) ----
    // Tenant-scoped paths (see environmentsPath's doc comment).

    async listEnvironments(params: PageParams = {}): Promise<Page<Environment>> {
      return request<Page<Environment>>({
        path: environmentsPath(),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async getEnvironment(envId: string): Promise<Environment> {
      return request<Environment>({ path: environmentPath(envId) });
    },

    async createEnvironment(input: CreateEnvironmentInput): Promise<Environment> {
      return request<Environment>({ method: "POST", path: environmentsPath(), body: input });
    },

    async updateEnvironment(envId: string, patch: UpdateEnvironmentInput): Promise<Environment> {
      return request<Environment>({ method: "PATCH", path: environmentPath(envId), body: patch });
    },

    async deleteEnvironment(envId: string): Promise<void> {
      await request<void>({ method: "DELETE", path: environmentPath(envId) });
    },

    async promoteService(envId: string, body: PromoteServiceInput): Promise<Deployment> {
      return request<Deployment>({ method: "POST", path: environmentPromotePath(envId), body });
    },

    // ---- LLM provider credentials (write-only key material — S-13/S-14) ----

    async listLlmCredentials(params: ListLlmCredentialsParams): Promise<Page<LlmCredentialSummary>> {
      return request<Page<LlmCredentialSummary>>({
        path: llmCredentialsPath(),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async createLlmCredential(
      input: CreateLlmCredentialInput,
    ): Promise<LlmCredentialSummary> {
      return request<LlmCredentialSummary>({
        method: "POST",
        path: llmCredentialsPath(),
        body: input,
      });
    },

    async rotateLlmCredentialKey(
      id: string,
      input: RotateLlmCredentialKeyInput,
    ): Promise<LlmCredentialSummary> {
      return request<LlmCredentialSummary>({
        method: "PUT",
        path: `${llmCredentialPath(id)}/key`,
        body: input,
      });
    },

    async updateLlmCredentialConfig(
      id: string,
      input: UpdateLlmCredentialConfigInput,
    ): Promise<LlmCredentialSummary> {
      return request<LlmCredentialSummary>({
        method: "PATCH",
        path: llmCredentialPath(id),
        body: input,
      });
    },

    async deleteLlmCredential(id: string): Promise<void> {
      await request<void>({ method: "DELETE", path: llmCredentialPath(id) });
    },

    // ---- Agentic workbench sessions ----

    async listAgentSessions(params: ListAgentSessionsParams): Promise<Page<AgentSession>> {
      return request<Page<AgentSession>>({
        path: agentSessionsPath(),
        // `local` is an ASSUMED filter param (no server-side support confirmed
        // yet — see ListAgentSessionsParams's doc comment); omitted from the
        // query string entirely when unset (buildUrl drops `undefined`), so
        // this is a no-op against a server that doesn't recognize it yet.
        query: { cursor: params.cursor, limit: params.limit, local: params.local ? "true" : undefined },
      });
    },

    async getAgentSession(id: string): Promise<AgentSession> {
      return request<AgentSession>({ path: agentSessionPath(id) });
    },

    async createAgentSession(
      input: CreateAgentSessionInput,
    ): Promise<AgentSession> {
      return request<AgentSession>({
        method: "POST",
        path: agentSessionsPath(),
        body: input,
      });
    },

    async stopAgentSession(id: string): Promise<AgentSession> {
      return request<AgentSession>({
        method: "POST",
        path: `${agentSessionPath(id)}/stop`,
      });
    },

    async deleteAgentSession(id: string): Promise<void> {
      await request<void>({ method: "DELETE", path: agentSessionPath(id) });
    },

    async runAgentSessionTask(id: string, input: RunSessionTaskInput): Promise<void> {
      await request<void>({
        method: "POST",
        path: `${agentSessionPath(id)}/task`,
        body: input,
      });
    },

    async promoteAgentSession(id: string, input: PromoteSessionInput): Promise<PromoteSessionResult> {
      return request<PromoteSessionResult>({
        method: "POST",
        path: `${agentSessionPath(id)}/promote`,
        body: input,
      });
    },

    async getProviderCatalog(credentialId: string): Promise<ProviderCatalog> {
      return request<ProviderCatalog>({
        path: providerCatalogPath(),
        query: { credentialId },
      });
    },

    // ---- Hero flow: instances (Hetzner provisioning; same
    // COMEBYE_PROVISIONING gating as provider credentials above) ----

    async listInstances(params: ListInstancesParams): Promise<Page<Instance>> {
      return request<Page<Instance>>({
        path: instancesPath(),
        query: { cursor: params.cursor, limit: params.limit, projectId: params.projectId },
      });
    },

    async getInstance(id: string): Promise<Instance> {
      return request<Instance>({ path: instancePath(id) });
    },

    async createInstance(input: CreateInstanceInput): Promise<Instance> {
      return request<Instance>({ method: "POST", path: instancesPath(), body: input });
    },

    async deleteInstance(id: string): Promise<void> {
      await request<void>({ method: "DELETE", path: instancePath(id) });
    },

    // ---- Org import: "bring your existing infra" (ADR-0048 §2, #472) ----

    async proposeImport(params: ProposeImportParams): Promise<ImportProposal> {
      return request<ImportProposal>({
        path: proposeImportPath(),
        query: { provider: params.provider, credentialId: params.credentialId },
      });
    },

    async applyImport(items: ProposedImportItem[]): Promise<ApplyImportResult> {
      return request<ApplyImportResult>({ method: "POST", path: applyImportPath(), body: { items } });
    },

    // ---- Billing (L-1) ----
    // No tenant/fleet segment on any of these paths, matching the server: the
    // tenant comes only from the session cookie (S-17), so nothing this client
    // sends can aim them at another tenant's billing.
    async getSubscription(): Promise<Subscription> {
      return request<Subscription>({ path: "/api/billing/subscription" });
    },

    async startCheckout(email?: string): Promise<string> {
      const { url } = await request<{ url: string }>({
        method: "POST",
        path: "/api/billing/checkout",
        body: email ? { email } : {},
      });
      return url;
    },

    async openBillingPortal(): Promise<string> {
      const { url } = await request<{ url: string }>({ method: "POST", path: "/api/billing/portal" });
      return url;
    },

    // ---- Licensing / entitlement (ADR-0017) ----
    // server/internal/entitlements mounts this at GET /api/entitlement, scoped
    // to the session's own tenant only (S-17) — same "no tenant parameter
    // anywhere" shape as the billing calls above.
    async getEntitlement(): Promise<EntitlementStatus> {
      return request<EntitlementStatus>({ path: "/api/entitlement" });
    },

    // ---- Fleet health matrix (ADR-0030 P4-2, read-only) ----

    async listHealthMatrix(params: ListHealthParams): Promise<Page<HealthCell>> {
      return request<Page<HealthCell>>({
        path: healthPath(),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    // ---- Fleet-health alerting (ADR-0030 P4-5) ----
    // server/internal/alertsvc mounts these under /api/alerts (tenant from the
    // session).

    async listAlertRules(params: ListAlertRulesParams): Promise<Page<AlertRule>> {
      return request<Page<AlertRule>>({
        path: alertsPath(),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async createAlertRule(input: CreateAlertRuleInput): Promise<AlertRule> {
      return request<AlertRule>({
        method: "POST",
        path: alertsPath(),
        body: {
          name: input.name,
          condition: input.condition,
          threshold: input.threshold,
          scope: input.scope ?? "",
          webhookUrl: input.webhookUrl ?? "",
          enabled: input.enabled,
          signingSecret:
            input.signingSecret !== undefined
              ? encodeSigningSecretBase64(input.signingSecret)
              : undefined,
        },
      });
    },

    async updateAlertRule(
      id: string,
      input: UpdateAlertRuleInput,
    ): Promise<AlertRule> {
      return request<AlertRule>({
        method: "PATCH",
        path: alertRulePath(id),
        body: {
          name: input.name,
          threshold: input.threshold,
          scope: input.scope,
          webhookUrl: input.webhookUrl,
          enabled: input.enabled,
          // undefined stays undefined (omitted from the JSON body -> "leave
          // unchanged" server-side); any string, including "", is encoded
          // and sent (see UpdateAlertRuleInput's doc comment in client.ts).
          signingSecret:
            input.signingSecret !== undefined
              ? encodeSigningSecretBase64(input.signingSecret)
              : undefined,
        },
      });
    },

    async deleteAlertRule(id: string): Promise<void> {
      await request<void>({ method: "DELETE", path: alertRulePath(id) });
    },

    async listAlertInstances(params: ListAlertInstancesParams): Promise<Page<AlertInstance>> {
      return request<Page<AlertInstance>>({
        path: alertInstancesPath(params.ruleId),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },
  };
}
